import * as z from 'zod';
import { statementTypeSchema, financialPeriodPreparationMethodSchema, extractionErrorTypeSchema, componentExtractionStatusSchema, dataSourceSchema, periodTypeSchema } from '../../../enums.mjs';
import '../../../types/actor/actor.mjs';
import '../../../types/address/address.mjs';
import '../../../types/credit-report/company-credit-report.mjs';
import '../../../types/credit-report/individual-credit-report.mjs';
import '../../../types/currency/currency.mjs';
import '../../../types/entityRelation/entity-relation.mjs';
import '../../../types/financial-formula/adjustable-value.mjs';
import { AdjustableValuesUpdate } from '../../../types/financial-formula/adjustable-values-update.mjs';
import '../../../types/financial-formula/adjustment.mjs';
import { formulaSchema } from '../../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../../types/is-encrypted.mjs';
import '../../../types/naics-codes/naics-codes.mjs';
import { isoDateTimeSchema, isoDateSchema, suretyAccountId, balanceSheetId, userId, fileId, financialExtractionId, financialPeriodId, incomeStatementId } from '../../../types/opaque.mjs';
import '../../../types/surety/account/bonding-line.mjs';
import '../../../types/surety/account/surety-account.types.mjs';
import '../../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../../types/surety/bond/surety-bond.types.mjs';
import '../../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../../types/surety/contract/contract-extraction-output.mjs';
import '../../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import { lineItemLabelSchema } from '../../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import { BalanceSheetLineItemPath } from '../../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import { BalanceSheetView } from '../../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../../types/surety/quote/surety-quote.types.mjs';
import '../../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../../types/user/user.types.mjs';
import { currencySchema } from '../../../types/utils.mjs';

var Get;
(function (Get) {
    (function (Response) {
        Response.schema = z.object({
            id: balanceSheetId,
            suretyAccountId: suretyAccountId,
            financialPeriodId: financialPeriodId.optional(),
            financialExtractionId: financialExtractionId.optional(),
            fileId: fileId.optional(),
            preparationMethod: financialPeriodPreparationMethodSchema,
            statementType: statementTypeSchema,
            statementDate: isoDateSchema,
            sourceStatementDate: isoDateSchema.optional(),
            sourceDataSource: dataSourceSchema.optional(),
            view: BalanceSheetView.schema.optional(),
            status: componentExtractionStatusSchema.optional(),
            errorType: extractionErrorTypeSchema.optional(),
            errorMessage: z.string().optional(),
            underwriterReviewedAt: isoDateTimeSchema.optional(),
            underwriterReviewedByUserId: userId.optional(),
            createdAt: isoDateTimeSchema,
            updatedAt: isoDateTimeSchema,
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var List;
(function (List) {
    (function (Response) {
        Response.schema = z.array(Get.Response.schema);
    })(List.Response || (List.Response = {}));
})(List || (List = {}));
var Update;
(function (Update) {
    (function (Request) {
        Request.schema = z.object({
            sectionPath: BalanceSheetLineItemPath.schema,
            label: lineItemLabelSchema,
            values: AdjustableValuesUpdate.schema,
        });
    })(Update.Request || (Update.Request = {}));
})(Update || (Update = {}));
var BulkDelete;
(function (BulkDelete) {
    (function (Request) {
        Request.schema = z.object({
            sectionPath: BalanceSheetLineItemPath.schema,
            label: lineItemLabelSchema,
            balanceSheetIds: z.array(balanceSheetId).min(1),
        });
    })(BulkDelete.Request || (BulkDelete.Request = {}));
})(BulkDelete || (BulkDelete = {}));
var CreateLineItem;
(function (CreateLineItem) {
    (function (Request) {
        const periodSchema = z
            .object({
            balanceSheetId: balanceSheetId,
            value: currencySchema.optional(),
            formula: formulaSchema.optional(),
        })
            .refine((p) => p.value !== undefined || p.formula !== undefined, {
            message: "At least one of value or formula must be provided",
        });
        Request.schema = z
            .object({
            sectionPath: BalanceSheetLineItemPath.schema,
            label: lineItemLabelSchema,
            periods: z.array(periodSchema).min(1),
        })
            .superRefine((data, ctx) => {
            const ids = data.periods.map((period) => period.balanceSheetId);
            const hasDuplicates = ids.some((id, index) => ids.indexOf(id) !== index);
            if (hasDuplicates) {
                ctx.addIssue({
                    code: "custom",
                    message: "Duplicate balance sheet IDs are not allowed",
                    path: ["periods"],
                });
            }
        });
    })(CreateLineItem.Request || (CreateLineItem.Request = {}));
})(CreateLineItem || (CreateLineItem = {}));
var RenameLineItem;
(function (RenameLineItem) {
    (function (Request) {
        Request.schema = z
            .object({
            sectionPath: BalanceSheetLineItemPath.schema,
            oldLabel: lineItemLabelSchema,
            newLabel: lineItemLabelSchema,
            balanceSheetIds: z.array(balanceSheetId).min(1),
        })
            .superRefine((data, ctx) => {
            if (data.oldLabel === data.newLabel) {
                ctx.addIssue({
                    code: "custom",
                    message: "New label must differ from old label",
                    path: ["newLabel"],
                });
            }
            const ids = data.balanceSheetIds;
            const hasDuplicates = ids.some((id, index) => ids.indexOf(id) !== index);
            if (hasDuplicates) {
                ctx.addIssue({
                    code: "custom",
                    message: "Duplicate balance sheet IDs are not allowed",
                    path: ["balanceSheetIds"],
                });
            }
        });
    })(RenameLineItem.Request || (RenameLineItem.Request = {}));
})(RenameLineItem || (RenameLineItem = {}));
var Create;
(function (Create) {
    (function (Request) {
        Request.schema = z.object({
            periodType: periodTypeSchema,
            statementDate: isoDateSchema,
            preparationMethod: financialPeriodPreparationMethodSchema,
        });
    })(Create.Request || (Create.Request = {}));
    (function (Response) {
        Response.schema = z.object({
            statement: Get.Response.schema,
            period: z.object({
                id: financialPeriodId,
                activeBalanceSheetId: balanceSheetId.optional(),
                activeIncomeStatementId: incomeStatementId.optional(),
            }),
        });
    })(Create.Response || (Create.Response = {}));
})(Create || (Create = {}));
var UpdatePeriod;
(function (UpdatePeriod) {
    (function (Request) {
        Request.schema = z.object({
            periodEndDate: isoDateSchema.optional(),
            periodType: periodTypeSchema.optional(),
            preparationMethod: financialPeriodPreparationMethodSchema.optional(),
        });
    })(UpdatePeriod.Request || (UpdatePeriod.Request = {}));
    (function (Response) {
        Response.schema = Get.Response.schema;
    })(UpdatePeriod.Response || (UpdatePeriod.Response = {}));
})(UpdatePeriod || (UpdatePeriod = {}));

export { BulkDelete, Create, CreateLineItem, Get, List, RenameLineItem, Update, UpdatePeriod };
